import { useNavigate } from 'react-router-dom';

const DigitalTransformationPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0D0D0D]">
      {/* Back Button */}
      <button
        onClick={() => { navigate('/'); window.scrollTo(0, 0); }}
        data-testid="back-to-home"
        className="fixed top-6 left-6 z-50 flex items-center space-x-2 text-white hover:text-[#00E600] transition-colors"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        <span className="font-semibold">Back to Home</span>
      </button>

      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center px-6 pt-24 pb-16">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1451187580459-43490279c0fa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxkaWdpdGFsJTIwdHJhbnNmb3JtYXRpb258ZW58MHx8fHwxNzYxMDQ2NzY0fDA&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}></div>
        
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 uppercase tracking-wide">
            Digital <span className="text-[#00E600]">Transformation</span>
          </h1>
          <p className="text-xl text-[#B3B3B3] leading-relaxed">
            Legacy dies. Intelligence prevails. AI-powered transformation that doesn't digitize the past—it engineers the future.
          </p>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 px-6 bg-[#1A1A1A]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-white mb-8 uppercase tracking-wide">Overview</h2>
          <div className="space-y-6 text-lg text-[#B3B3B3] leading-relaxed">
            <p className="text-xl text-white font-semibold">
              Most companies digitize. Winners transform. The difference? Digitization automates yesterday's 
              processes. Transformation reimagines tomorrow's business model. One saves costs. The other creates 
              competitive advantage competitors can't copy.
            </p>
            <p>
              NYAXO's Digital Transformation Consulting doesn't hand you a technology roadmap—we architect intelligent 
              enterprises. We identify AI opportunities hidden in your operations, eliminate technical debt strangling 
              innovation, and design execution strategies that compound value over time, not just cut costs.
            </p>
            <p>
              We've powered $500M+ in transformation value across Global 2000 enterprises. From legacy banking 
              systems to cloud-native fintech. From manual factories to Industry 4.0 operations. We don't 
              digitize the past—we engineer futures that scale.
            </p>
          </div>
        </div>
      </section>

      {/* Consulting Services */}
      <section className="py-16 px-6 bg-[#0D0D0D]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4 uppercase tracking-wide">
              Transformation <span className="text-[#00E600]">Services</span>
            </h2>
            <p className="text-[#B3B3B3] text-lg max-w-3xl mx-auto">
              End-to-end consulting that turns strategic vision into operational reality
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg p-6 hover:border-[#00E600] transition-all duration-300 group">
              <div className="w-12 h-12 bg-gradient-to-br from-[#00E600] to-[#36E0A8] rounded-lg flex items-center justify-center mb-4 group-hover:shadow-[0_0_20px_rgba(0,230,0,0.5)] transition-all duration-300">
                <svg className="w-6 h-6 text-[#0D0D0D]" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Strategy & Roadmap</h3>
              <ul className="space-y-2 text-sm text-[#B3B3B3]">
                <li>• AI opportunity assessment & business case</li>
                <li>• Technology stack evaluation & selection</li>
                <li>• Phased implementation roadmap</li>
                <li>• ROI modeling & KPI definition</li>
              </ul>
            </div>

            <div className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg p-6 hover:border-[#00E600] transition-all duration-300 group">
              <div className="w-12 h-12 bg-gradient-to-br from-[#00E600] to-[#36E0A8] rounded-lg flex items-center justify-center mb-4 group-hover:shadow-[0_0_20px_rgba(0,230,0,0.5)] transition-all duration-300">
                <svg className="w-6 h-6 text-[#0D0D0D]" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2 5a2 2 0 012-2h12a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V5zm3.293 1.293a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 01-1.414-1.414L7.586 10 5.293 7.707a1 1 0 010-1.414zM11 12a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Legacy Modernization</h3>
              <ul className="space-y-2 text-sm text-[#B3B3B3]">
                <li>• Technical debt assessment & remediation</li>
                <li>• Cloud migration strategies (AWS, Azure, GCP)</li>
                <li>• Microservices & API architecture</li>
                <li>• Zero-downtime migration execution</li>
              </ul>
            </div>

            <div className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg p-6 hover:border-[#00E600] transition-all duration-300 group">
              <div className="w-12 h-12 bg-gradient-to-br from-[#00E600] to-[#36E0A8] rounded-lg flex items-center justify-center mb-4 group-hover:shadow-[0_0_20px_rgba(0,230,0,0.5)] transition-all duration-300">
                <svg className="w-6 h-6 text-[#0D0D0D]" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 3.5a1.5 1.5 0 013 0V4a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-.5a1.5 1.5 0 000 3h.5a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-.5a1.5 1.5 0 00-3 0v.5a1 1 0 01-1 1H6a1 1 0 01-1-1v-3a1 1 0 00-1-1h-.5a1.5 1.5 0 010-3H4a1 1 0 001-1V6a1 1 0 011-1h3a1 1 0 001-1v-.5z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">AI Integration</h3>
              <ul className="space-y-2 text-sm text-[#B3B3B3]">
                <li>• Machine learning model deployment</li>
                <li>• Process automation identification</li>
                <li>• AI-powered decision systems</li>
                <li>• MLOps & model governance</li>
              </ul>
            </div>

            <div className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg p-6 hover:border-[#00E600] transition-all duration-300 group">
              <div className="w-12 h-12 bg-gradient-to-br from-[#00E600] to-[#36E0A8] rounded-lg flex items-center justify-center mb-4 group-hover:shadow-[0_0_20px_rgba(0,230,0,0.5)] transition-all duration-300">
                <svg className="w-6 h-6 text-[#0D0D0D]" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                  <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm9.707 5.707a1 1 0 00-1.414-1.414L9 12.586l-1.293-1.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Process Optimization</h3>
              <ul className="space-y-2 text-sm text-[#B3B3B3]">
                <li>• Workflow analysis & redesign</li>
                <li>• Bottleneck identification & elimination</li>
                <li>• Automation opportunity mapping</li>
                <li>• Performance benchmarking & tracking</li>
              </ul>
            </div>

            <div className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg p-6 hover:border-[#00E600] transition-all duration-300 group">
              <div className="w-12 h-12 bg-gradient-to-br from-[#00E600] to-[#36E0A8] rounded-lg flex items-center justify-center mb-4 group-hover:shadow-[0_0_20px_rgba(0,230,0,0.5)] transition-all duration-300">
                <svg className="w-6 h-6 text-[#0D0D0D]" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Change Management</h3>
              <ul className="space-y-2 text-sm text-[#B3B3B3]">
                <li>• Stakeholder engagement & alignment</li>
                <li>• Organizational readiness assessment</li>
                <li>• Training program development</li>
                <li>• Adoption monitoring & support</li>
              </ul>
            </div>

            <div className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg p-6 hover:border-[#00E600] transition-all duration-300 group">
              <div className="w-12 h-12 bg-gradient-to-br from-[#00E600] to-[#36E0A8] rounded-lg flex items-center justify-center mb-4 group-hover:shadow-[0_0_20px_rgba(0,230,0,0.5)] transition-all duration-300">
                <svg className="w-6 h-6 text-[#0D0D0D]" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11 4a1 1 0 10-2 0v4a1 1 0 102 0V7zm-3 1a1 1 0 10-2 0v3a1 1 0 102 0V8zM8 9a1 1 0 00-2 0v2a1 1 0 102 0V9z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Continuous Improvement</h3>
              <ul className="space-y-2 text-sm text-[#B3B3B3]">
                <li>• Post-implementation review & optimization</li>
                <li>• KPI tracking & performance analytics</li>
                <li>• Next-generation opportunity identification</li>
                <li>• Innovation pipeline development</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Problems We Solve */}
      <section className="py-16 px-6 bg-[#1A1A1A]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4 uppercase tracking-wide">
              Transformation <span className="text-[#00E600]">Challenges</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-[#0D0D0D] border border-[#2F2F2F] rounded-lg p-8 hover:border-[#00E600] transition-all duration-300">
              <h3 className="text-xl font-bold text-white mb-4">Legacy System Paralysis</h3>
              <p className="text-[#B3B3B3] mb-3">
                <span className="text-white font-semibold">Problem:</span> Decades-old systems cost 60-80% of IT budgets to maintain, strangling innovation and preventing modernization.
              </p>
              <p className="text-[#00E600] font-semibold">
                → Solution: Phased migration strategies with zero-downtime transitions, retiring technical debt while maintaining business continuity.
              </p>
            </div>

            <div className="bg-[#0D0D0D] border border-[#2F2F2F] rounded-lg p-8 hover:border-[#00E600] transition-all duration-300">
              <h3 className="text-xl font-bold text-white mb-4">Failed Transformation Projects</h3>
              <p className="text-[#B3B3B3] mb-3">
                <span className="text-white font-semibold">Problem:</span> 70% of digital initiatives fail, wasting $900B+ annually on transformation projects that never deliver.
              </p>
              <p className="text-[#00E600] font-semibold">
                → Solution: Proven transformation frameworks with clear success metrics, phased rollouts, and continuous validation.
              </p>
            </div>

            <div className="bg-[#0D0D0D] border border-[#2F2F2F] rounded-lg p-8 hover:border-[#00E600] transition-all duration-300">
              <h3 className="text-xl font-bold text-white mb-4">Organizational Resistance</h3>
              <p className="text-[#B3B3B3] mb-3">
                <span className="text-white font-semibold">Problem:</span> 80% of transformation failures stem from people, not technology—resistance, fear, and lack of skills derail initiatives.
              </p>
              <p className="text-[#00E600] font-semibold">
                → Solution: Comprehensive change management with executive sponsorship, training programs, and adoption monitoring.
              </p>
            </div>

            <div className="bg-[#0D0D0D] border border-[#2F2F2F] rounded-lg p-8 hover:border-[#00E600] transition-all duration-300">
              <h3 className="text-xl font-bold text-white mb-4">ROI Uncertainty</h3>
              <p className="text-[#B3B3B3] mb-3">
                <span className="text-white font-semibold">Problem:</span> Executives can't quantify transformation value, leading to budget cuts and premature project termination.
              </p>
              <p className="text-[#00E600] font-semibold">
                → Solution: AI-powered ROI modeling, real-time impact tracking, and business case validation at each milestone.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-16 px-6 bg-[#0D0D0D]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4 uppercase tracking-wide">
              Industries We <span className="text-[#00E600]">Transform</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { name: 'Financial Services', challenge: 'Regulatory complexity & legacy core', benefit: '$500M+ value created, zero-downtime migrations' },
              { name: 'Healthcare', challenge: 'Data silos & compliance', benefit: 'HIPAA-compliant AI, 40% operational efficiency' },
              { name: 'Manufacturing', challenge: 'OT/IT integration', benefit: 'Industry 4.0, 25% production increase' },
              { name: 'Retail & E-commerce', challenge: 'Omnichannel complexity', benefit: 'AI personalization, 35% revenue lift' },
              { name: 'Energy & Utilities', challenge: 'Asset-intensive operations', benefit: 'Predictive maintenance, 30% cost reduction' },
              { name: 'Government & Public Sector', challenge: 'Legacy modernization at scale', benefit: 'Citizen service transformation, 50% efficiency' }
            ].map((industry, idx) => (
              <div key={idx} className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg p-6 hover:border-[#00E600] transition-all duration-300 group">
                <div className="w-10 h-10 bg-gradient-to-br from-[#00E600]/20 to-[#36E0A8]/20 rounded-lg flex items-center justify-center mb-4 border border-[#00E600]/30 group-hover:border-[#00E600] transition-all duration-300">
                  <div className="w-2 h-2 bg-[#00E600] rounded-full"></div>
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{industry.name}</h3>
                <p className="text-sm text-[#B3B3B3] mb-2"><span className="text-white font-semibold">Challenge:</span> {industry.challenge}</p>
                <p className="text-sm text-[#00E600] font-semibold">→ {industry.benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why NYAXO */}
      <section className="py-16 px-6 bg-[#1A1A1A]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4 uppercase tracking-wide">
              Why <span className="text-[#00E600]">NYAXO</span> Succeeds
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-[#0D0D0D] border-l-4 border-[#00E600] rounded-lg p-8">
              <h3 className="text-2xl font-bold text-white mb-6">Transformation Expertise</h3>
              <div className="space-y-4 text-[#B3B3B3]">
                <p><span className="text-[#00E600]">→</span> <span className="text-white font-semibold">89% Success Rate:</span> vs. 30% industry average—proven methodologies that work</p>
                <p><span className="text-[#00E600]">→</span> <span className="text-white font-semibold">$500M+ Value Created:</span> Measurable ROI across Global 2000 transformations</p>
                <p><span className="text-[#00E600]">→</span> <span className="text-white font-semibold">AI-First Approach:</span> Not just digitizing—intelligently reimagining business models</p>
                <p><span className="text-[#00E600]">→</span> <span className="text-white font-semibold">End-to-End Support:</span> Strategy through execution, training, and continuous optimization</p>
                <p><span className="text-[#00E600]">→</span> <span className="text-white font-semibold">Industry-Specific Playbooks:</span> Pre-built frameworks for your sector</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-[#0D0D0D] border border-[#2F2F2F] rounded-lg p-6">
                <h4 className="text-xl font-bold text-white mb-2">Success Rate</h4>
                <div className="text-4xl font-bold text-[#00E600] mb-2">89%</div>
                <p className="text-[#B3B3B3]">Transformations meeting or exceeding goals</p>
              </div>
              <div className="bg-[#0D0D0D] border border-[#2F2F2F] rounded-lg p-6">
                <h4 className="text-xl font-bold text-white mb-2">ROI Achievement</h4>
                <div className="text-4xl font-bold text-[#00E600] mb-2">250%</div>
                <p className="text-[#B3B3B3]">Average return on transformation investment</p>
              </div>
              <div className="bg-[#0D0D0D] border border-[#2F2F2F] rounded-lg p-6">
                <h4 className="text-xl font-bold text-white mb-2">Time to Value</h4>
                <div className="text-4xl font-bold text-[#00E600] mb-2">6mo</div>
                <p className="text-[#B3B3B3]">Average time to measurable business impact</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Transformation Phases */}
      <section className="py-16 px-6 bg-[#1A1A1A]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-white mb-12 uppercase tracking-wide">
            Our <span className="text-[#00E600]">Transformation Framework</span>
          </h2>
          <div className="space-y-6">
            {[
              { phase: '01', title: 'Discovery & Assessment', desc: 'Deep dive into current state, pain points, and transformation potential' },
              { phase: '02', title: 'Strategy & Roadmap', desc: 'AI-driven transformation blueprint with clear milestones and ROI projections' },
              { phase: '03', title: 'Pilot & Validation', desc: 'Proof-of-concept deployment to validate approach and refine strategy' },
              { phase: '04', title: 'Scale & Deploy', desc: 'Full-scale implementation with continuous monitoring and optimization' },
              { phase: '05', title: 'Optimize & Evolve', desc: 'Ongoing improvement and identification of next-level opportunities' }
            ].map((item, index) => (
              <div key={index} className="bg-[#0D0D0D] border border-[#2F2F2F] rounded-lg p-6 flex items-start space-x-6 hover:border-[#00E600] transition-all duration-300">
                <div className="text-4xl font-bold text-[#00E600]">{item.phase}</div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                  <p className="text-[#B3B3B3]">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Software Solutions */}
      <section className="py-16 px-6 bg-[#1A1A1A]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-white mb-8 uppercase tracking-wide">
            Our <span className="text-[#00E600]">Software Solutions</span>
          </h2>
          <div className="bg-[#0D0D0D] border border-[#2F2F2F] rounded-lg p-8">
            <p className="text-lg text-[#B3B3B3] mb-6">
              NYAXO has developed proprietary frameworks, tools, and accelerators that have powered $500M+ in 
              transformation value across Global 2000 enterprises.
            </p>
            <div className="border-l-4 border-[#00E600] pl-6 py-4">
              <p className="text-white font-semibold mb-2">📦 Custom Software Portfolio</p>
              <p className="text-[#B3B3B3] italic">
                [Your specific digital transformation tools and frameworks will be listed here]
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 bg-[#0D0D0D]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Begin Your Transformation</h2>
          <p className="text-lg text-[#B3B3B3] mb-8">
            Don't digitize the past. Engineer the future with NYAXO.
          </p>
          <button
            onClick={() => navigate('/#contact')}
            className="px-8 py-4 bg-gradient-to-r from-[#00E600] to-[#36E0A8] text-[#0D0D0D] font-bold rounded-md hover:shadow-[0_0_30px_rgba(0,230,0,0.6)] transition-all duration-300 uppercase tracking-wide"
          >
            Schedule Strategy Session
          </button>
        </div>
      </section>
    </div>
  );
};

export default DigitalTransformationPage;
